import React from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Security as SecurityIcon,
  DataUsage as DataUsageIcon,
  Cookie as CookieIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageSEO from '../components/PageSEO';

const PrivacyPage: React.FC = () => {
  const { t } = useTranslation();

  const sections = [
    {
      icon: <DataUsageIcon />,
      title: t('privacy.dataCollection'),
      content: t('privacy.dataCollectionItems', { returnObjects: true }) as string[],
    },
    {
      icon: <SecurityIcon />,
      title: t('privacy.dataUsage'),
      content: t('privacy.dataUsageItems', { returnObjects: true }) as string[],
    },
    {
      icon: <ShieldIcon />,
      title: t('privacy.dataProtection'),
      content: t('privacy.dataProtectionItems', { returnObjects: true }) as string[],
    },
    {
      icon: <CookieIcon />,
      title: t('privacy.cookies'),
      content: t('privacy.cookiesItems', { returnObjects: true }) as string[],
    },
  ];

  const rights = t('privacy.userRightsItems', { returnObjects: true }) as string[];

  return (
    <Box sx={{ backgroundColor: '#0f0f23', minHeight: '100vh' }}>
      <PageSEO pageKey="privacy" />
      <Header />
      
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 800,
              mb: 3,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              lineHeight: { xs: 1.2, md: 1.3 },
              background: 'linear-gradient(135deg, #ffffff 0%, #28A0F0 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
{t('privacy.title')}
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#a0a0a0',
              mb: 4,
              maxWidth: 800,
              mx: 'auto',
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              lineHeight: { xs: 1.4, md: 1.5 },
              px: { xs: 2, sm: 0 },
            }}
          >
{t('privacy.subtitle')}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#a0a0a0',
              fontSize: { xs: '0.8rem', md: '0.875rem' },
            }}
          >
{t('privacy.lastUpdated')}: {new Date().toLocaleDateString('bg-BG')}
          </Typography>
        </Box>

        {/* Introduction */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(40, 160, 240, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(40, 160, 240, 0.3)',
            borderRadius: 4,
            mb: 6,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('privacy.introduction')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('privacy.introText1')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('privacy.introText2')}
            </Typography>
          </CardContent>
        </Card>

        {/* Main Sections */}
        {sections.map((section, index) => (
          <Card
            key={index}
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: 4,
              mb: 4,
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    color: '#28A0F0',
                    mr: 2,
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: 32, md: 40 },
                    },
                  }}
                >
                  {section.icon}
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                  }}
                >
                  {section.title}
                </Typography>
              </Box>
              <List>
                {section.content.map((item, itemIndex) => (
                  <ListItem key={itemIndex} sx={{ px: 0, py: 0.5 }}>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#d0d0d0',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            lineHeight: 1.6,
                          }}
                        >
                          {item}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        ))}

        {/* User Rights */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(40, 160, 240, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: 4,
            mb: 4,
            overflow: 'hidden',
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '1.25rem', md: '1.5rem' },
              }}
            >
{t('privacy.userRights')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#d0d0d0',
                mb: 3,
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
              }}
            >
              {t('privacy.gdprRights')}
            </Typography>
            <List>
              {rights.map((right, index) => (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          color: '#d0d0d0',
                          fontSize: { xs: '0.9rem', md: '1rem' },
                          lineHeight: 1.6,
                        }}
                      >
                        • {right}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

      </Container>

      <Footer />
    </Box>
  );
};

export default PrivacyPage;
